import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Gauge, TrendingUp, Clock, AlertTriangle, FileSignature, Megaphone,
  ArrowRight, RefreshCw, Building2, Euro, CheckCircle2, XCircle, Loader2, Calculator,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const fmtEur = (n) => {
  if (n === null || n === undefined) return '—';
  return `${Math.round(Number(n)).toLocaleString('fr-FR')} €`;
};

const timeAgo = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return "à l'instant";
  const min = Math.floor(sec / 60);
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  return `il y a ${d} j`;
};

const Delta = ({ current, previous }) => {
  if (previous === 0 || previous === null || previous === undefined) return null;
  const diff = current - previous;
  if (diff === 0) return <span className="text-xs text-slate-400">=</span>;
  const up = diff > 0;
  return (
    <span className={`text-xs font-semibold ${up ? 'text-emerald-600' : 'text-red-500'}`}>
      {up ? '▲' : '▼'} {Math.abs(diff)}
    </span>
  );
};

export default function CockpitPage() {
  const { api, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const res = await api().get('/admin/cockpit');
      setData(res.data);
    } catch (e) {
      toast.error('Impossible de charger le Cockpit');
    } finally {
      setLoading(false);
    }
  };

  const runReminder = async () => {
    setRunning(true);
    try {
      const res = await api().post('/admin/jobs/signature-deadline/run');
      toast.success(`Relance envoyée (${res.data?.emails_sent ?? 0} email·s)`);
    } catch (e) {
      toast.error('Échec de la relance');
    } finally {
      setRunning(false);
    }
  };

  const recalcValidated = async () => {
    setRecalculating(true);
    try {
      const res = await api().post('/admin/simulations/recalculate-validated');
      const { scanned = 0, updated = 0, errors = 0 } = res.data || {};
      toast.success(`Recalcul terminé — ${updated}/${scanned} simulation·s mise·s à jour${errors ? ` (${errors} erreur·s)` : ''}`);
      fetchData();
    } catch (e) {
      toast.error('Échec du recalcul des simulations validées');
    } finally {
      setRecalculating(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]" data-testid="cockpit-loading">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </AppLayout>
    );
  }

  const cur = data?.current_month || {};
  const last = data?.last_month || {};
  const pipeline = data?.pipeline || {};
  const todo = data?.todo || {};
  const alerts = data?.deadline_alerts || [];
  const trend = data?.trend || [];

  return (
    <AppLayout>
      <div className="space-y-8" data-testid="cockpit-page">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-slate-900 dark:bg-slate-700 flex items-center justify-center">
              <Gauge className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Cockpit Direction</h1>
              <p className="text-sm text-slate-500">Bonjour {user?.name} — vue d'ensemble en temps réel</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                onClick={recalcValidated}
                disabled={recalculating}
                className="gap-2"
                data-testid="cockpit-recalculate-validated"
              >
                {recalculating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
                Recalculer les validées
              </Button>
              <Button variant="outline" onClick={fetchData} className="gap-2" data-testid="cockpit-refresh">
                <RefreshCw className="w-4 h-4" /> Actualiser
              </Button>
            </div>
            {data?.last_recalc?.at && (
              <span className="text-xs text-slate-400 flex items-center gap-1" data-testid="cockpit-last-recalc">
                <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                Chiffres à jour — dernier recalcul {timeAgo(data.last_recalc.at)}
              </span>
            )}
          </div>
        </div>

        {/* À traiter maintenant */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card
            className="border-slate-200 dark:border-slate-800 cursor-pointer hover:shadow-sm transition-shadow"
            onClick={() => navigate('/admin')}
            data-testid="todo-pending-decisions"
          >
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-lg bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">{todo.pending_decisions ?? 0}</p>
                <p className="text-sm text-slate-500">Décisions en attente</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300" />
            </CardContent>
          </Card>

          <Card
            className="border-slate-200 dark:border-slate-800 cursor-pointer hover:shadow-sm transition-shadow"
            onClick={() => navigate('/announcements')}
            data-testid="todo-pending-announcements"
          >
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
                <Megaphone className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <p className="text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">{todo.pending_announcements ?? 0}</p>
                <p className="text-sm text-slate-500">Annonces à valider</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300" />
            </CardContent>
          </Card>

          <Card className="border-slate-200 dark:border-slate-800" data-testid="todo-deadline-alerts">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-lg bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <p className="text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">{data?.deadline_alerts_count ?? 0}</p>
                <p className="text-sm text-slate-500">Dossiers à signer (J-3 mois)</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pipeline */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card data-testid="pipeline-deals">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-slate-500 text-sm mb-1"><Building2 className="w-4 h-4" /> Dossiers en cours</div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{pipeline.open_deals ?? 0}</p>
            </CardContent>
          </Card>
          <Card data-testid="pipeline-ca">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-slate-500 text-sm mb-1"><Euro className="w-4 h-4" /> CA HT pipeline</div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white font-mono">{fmtEur(pipeline.ca_pipeline)}</p>
            </CardContent>
          </Card>
          <Card data-testid="pipeline-capex">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-slate-500 text-sm mb-1"><TrendingUp className="w-4 h-4" /> CAPEX engagé</div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white font-mono">{fmtEur(pipeline.capex_pipeline)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Trend chart (6 months) */}
        <Card data-testid="cockpit-trend">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Tendance — 6 derniers mois</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={trend} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: 'rgba(148,163,184,0.1)' }} />
                  <Legend wrapperStyle={{ fontSize: 13 }} />
                  <Bar dataKey="created" name="Créées" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={36} />
                  <Bar dataKey="validated" name="Validées" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Performance mois en cours vs mois dernier */}
        <Card data-testid="cockpit-performance">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-500 text-left">
                    <th className="py-2"></th>
                    <th className="py-2 text-center">Créées</th>
                    <th className="py-2 text-center">Validées</th>
                    <th className="py-2 text-center">Refusées</th>
                    <th className="py-2 text-center">Taux GO</th>
                    <th className="py-2 text-right">CA validé</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-3 font-semibold text-slate-900 dark:text-white">{cur.label} <Badge className="ml-1 bg-blue-100 text-blue-700 text-xs">en cours</Badge></td>
                    <td className="py-3 text-center font-bold">{cur.created} <Delta current={cur.created} previous={last.created} /></td>
                    <td className="py-3 text-center font-bold text-emerald-600">{cur.validated}</td>
                    <td className="py-3 text-center font-bold text-red-500">{cur.refused}</td>
                    <td className="py-3 text-center font-bold">{cur.go_rate}%</td>
                    <td className="py-3 text-right font-bold font-mono">{fmtEur(cur.ca_validated)}</td>
                  </tr>
                  <tr className="border-t border-slate-100 dark:border-slate-800 text-slate-500">
                    <td className="py-3">{last.label}</td>
                    <td className="py-3 text-center">{last.created}</td>
                    <td className="py-3 text-center">{last.validated}</td>
                    <td className="py-3 text-center">{last.refused}</td>
                    <td className="py-3 text-center">{last.go_rate}%</td>
                    <td className="py-3 text-right font-mono">{fmtEur(last.ca_validated)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Deadline alerts */}
        <Card data-testid="cockpit-deadline-table">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2 text-red-600">
              <FileSignature className="w-5 h-5" /> Dossiers à signer rapidement (J-3 mois)
            </CardTitle>
            <Button onClick={runReminder} disabled={running} size="sm" className="gap-2 bg-orange-500 hover:bg-orange-600" data-testid="cockpit-run-reminder">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
              Relancer maintenant
            </Button>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <div className="flex items-center gap-2 text-emerald-600 py-4">
                <CheckCircle2 className="w-5 h-5" /> Aucun dossier en risque de délai. 🎉
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-500 text-left">
                      <th className="py-2">Dossier</th>
                      <th className="py-2">Ouverture prévue</th>
                      <th className="py-2 text-center">Échéance</th>
                      <th className="py-2 text-center">Ouvert depuis</th>
                      <th className="py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {alerts.map((d) => (
                      <tr key={d.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="py-3">
                          <div className="font-medium text-slate-900 dark:text-white">{`${d.retailer} ${d.city}`.trim() || d.project_name}</div>
                          <div className="text-xs text-slate-500">{d.project_name}</div>
                        </td>
                        <td className="py-3 font-mono">{d.opening_date}</td>
                        <td className="py-3 text-center">
                          {d.days_until_opening < 0 ? (
                            <Badge className="bg-red-100 text-red-700">En retard ({Math.abs(d.days_until_opening)} j)</Badge>
                          ) : (
                            <Badge className="bg-orange-100 text-orange-700">J-{d.days_until_opening}</Badge>
                          )}
                        </td>
                        <td className="py-3 text-center text-slate-500">{d.age_days != null ? `${d.age_days} j` : '—'}</td>
                        <td className="py-3 text-right">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/simulation/${d.id}`)} data-testid={`cockpit-open-${d.id}`}>
                            Ouvrir <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
